import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { MaintenanceWindow } from "../types";
import { supportedTimeZones, browserTimeZone } from "../utils/datetime";
import ActionButton from "./ActionButton";

/* Per-customer maintenance window editor. Approved patches install (and
 * optional reboots happen) during this window. Compact + reusable so it can
 * sit on the Patch Compliance page right where "installs at the next
 * maintenance window" is shown. */

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // index = Monday=0

export default function MaintenanceWindowCard({
  customerId,
  canEdit,
}: {
  customerId: number;
  canEdit: boolean;
}) {
  const [win, setWin] = useState<MaintenanceWindow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/api/v1/maintenance-windows/customers/${customerId}`)
      .then((r) => setWin(r.data))
      .catch(() => setWin(null))
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (p: Partial<MaintenanceWindow>) => setWin((w) => (w ? { ...w, ...p } : w));
  const toggleDay = (d: number) =>
    setWin((w) =>
      w
        ? {
            ...w,
            days_of_week: w.days_of_week.includes(d)
              ? w.days_of_week.filter((x) => x !== d)
              : [...w.days_of_week, d].sort((a, b) => a - b),
          }
        : w,
    );

  const save = async () => {
    if (!win) return;
    setError("");
    try {
      const r = await api.put(`/api/v1/maintenance-windows/customers/${customerId}`, {
        enabled: win.enabled,
        days_of_week: win.days_of_week,
        start_time: win.start_time,
        end_time: win.end_time,
        timezone: win.timezone || browserTimeZone(),
        allow_reboot: win.allow_reboot,
        reboot_deferral_seconds: win.reboot_deferral_seconds ?? 300,
        reboot_message: win.reboot_message,
      });
      setWin(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Could not save the maintenance window.");
    }
  };

  const summary = () => {
    if (!win) return "";
    if (!win.enabled || (win.days_of_week || []).length === 0) {
      return "No active window — approved patches will NOT auto-install until one is set.";
    }
    const days = win.days_of_week.map((d) => DOW[d]).join(", ");
    return `Approved patches install ${days} ${win.start_time}–${win.end_time} (${win.timezone})${
      win.allow_reboot ? ", reboots allowed" : ", no reboots"
    }.`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          🛠 Maintenance Window
        </h2>
        {win && (
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={win.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
            />
            Enabled
          </label>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        When approved patches (and optional reboots) install for this customer.{" "}
        <span className={win && (!win.enabled || !win.days_of_week.length) ? "text-amber-600 dark:text-amber-400" : ""}>
          {loading ? "Loading…" : summary()}
        </span>
      </p>

      {win && (
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <span className="block text-xs text-gray-500 mb-1">Days</span>
            <div className="flex gap-1">
              {DOW.map((d, i) => (
                <button
                  key={d}
                  disabled={!canEdit}
                  onClick={() => toggleDay(i)}
                  className={`text-xs px-2 py-1 rounded ${
                    win.days_of_week.includes(i)
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                  } disabled:opacity-50`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <label className="text-xs">
            <span className="block text-gray-500 mb-1">Start</span>
            <input
              type="time"
              disabled={!canEdit}
              value={win.start_time}
              onChange={(e) => patch({ start_time: e.target.value })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1"
            />
          </label>
          <label className="text-xs">
            <span className="block text-gray-500 mb-1">End</span>
            <input
              type="time"
              disabled={!canEdit}
              value={win.end_time}
              onChange={(e) => patch({ end_time: e.target.value })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1"
            />
          </label>
          <label className="text-xs">
            <span className="block text-gray-500 mb-1">Time zone</span>
            <select
              disabled={!canEdit}
              value={win.timezone}
              onChange={(e) => patch({ timezone: e.target.value })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 max-w-[14rem]"
            >
              {supportedTimeZones().map((z) => (
                <option key={z} value={z}>
                  {z.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 pb-1">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={win.allow_reboot}
              onChange={(e) => patch({ allow_reboot: e.target.checked })}
            />
            Allow reboots
          </label>
          {canEdit && (
            <ActionButton onAction={() => save()} variant="raw" className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700" pendingLabel="Saving…">
              {saved ? "Saved ✓" : "Save"}
            </ActionButton>
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
    </div>
  );
}
